import { Spinner } from '@campx-org/shared'
import { Add, ChevronLeft, ChevronRight, Remove } from '@mui/icons-material'
import { Checkbox, Stack, Typography } from '@mui/material'
import * as pdfjsLib from 'pdfjs-dist'
import 'pdfjs-dist/web/pdf_viewer.css'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { EditorStore } from 'store/editor'
import { useImmer } from 'use-immer'
import { EvaluatorEditorStore } from './EvaluatorEditor'
import { StyledActionsContainer } from './QuestionsPanel'
import {
  DarkTooltip,
  StyledBookletContainer,
  StyledCheckBoxStack,
  StyledIconButton,
  StyledPdfContainer,
  StyledZoomControlsContainer,
  StyledZoomIconButton,
} from './Styles'

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs'

/**
 * Component that renders a booklet view with actions and a PDF viewer.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.bookletActions - The actions to be displayed in the booklet.
 * @param {string} props.pdfUrl - The URL of the PDF to be displayed.
 *
 * @returns {JSX.Element} The rendered component.
 */
function BookletView({
  bookletActions,
  pdfUrl,
}: {
  bookletActions: ReactNode
  pdfUrl: string
}): JSX.Element {
  return (
    <StyledBookletContainer>
      <BookletActions actions={bookletActions} />
      <PdfViewer pdfUrl={pdfUrl} />
    </StyledBookletContainer>
  )
}

export default BookletView

/**
 * Component that renders a container for booklet actions with a toggleable panel.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.actions - The actions to be displayed within the container.
 *
 * @returns {JSX.Element} The rendered component.
 * @component
 */
function BookletActions({ actions }: { actions: ReactNode }): JSX.Element {
  const { panelOpen, zoomLevel } = EvaluatorEditorStore.useState()

  const togglePanel = () => {
    EvaluatorEditorStore.update((s) => {
      // If the panel is closed or the zoom level isn't 1.8,
      // reset zoom to 1.8 and open the panel.
      if (!s.panelOpen || s.zoomLevel !== 1.8) {
        s.zoomLevel = 1.8
        s.panelOpen = true
      } else {
        // Otherwise, close the panel and set zoom to 2.6.
        s.zoomLevel = 2.6
        s.panelOpen = false
      }
    })
  }
  return (
    <StyledActionsContainer>
      {actions}
      <DarkTooltip title={panelOpen ? 'Hide Panel' : 'Show Panel'}>
        <StyledIconButton onClick={togglePanel}>
          {panelOpen && zoomLevel <= 1.8 ? <ChevronRight /> : <ChevronLeft />}
        </StyledIconButton>
      </DarkTooltip>
    </StyledActionsContainer>
  )
}

/**
 * PdfViewer component renders a PDF document and provides zoom controls.
 *
 * @param {Object} props - The component props.
 * @param {string} props.pdfUrl - The URL of the PDF document to be displayed.
 *
 * @returns {JSX.Element} The rendered PDF viewer component.
 */
export const PdfViewer = ({ pdfUrl }: { pdfUrl: string }): JSX.Element => {
  // Check if the PDF URL is available
  if (!pdfUrl) {
    return <Typography textAlign="center">PDF NOT FOUND</Typography>
  }
  const [state, setState] = useImmer({
    numPages: 0,
    isLoading: true,
    isRenderingPages: false,
    loadingError: null as string | null,
    totalHeight: 0,
  })

  const { zoomLevel, hasReviewedAllPages } = EvaluatorEditorStore.useState()
  const { booklet } = EditorStore.useState()

  // Refs for the canvas container and the PDF document
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const pdfRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)

  // Check if the user is on a mobile device
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor
    setIsMobile(/android|iPad|iPhone|iPod/i.test(userAgent))
  }, [])

  // Render the PDF pages
  const renderPages = async () => {
    // Check if the PDF document and canvas container are available and if the pages are already being rendered
    if (
      !pdfRef.current ||
      state.isRenderingPages ||
      !canvasContainerRef.current
    ) {
      return
    }

    // Clear the canvas container and set the state to indicate that the pages are being rendered
    setState((s) => {
      s.isRenderingPages = true
    })

    // Get the PDF document
    const pdf = pdfRef.current
    // Set the number of pages
    setState((s) => {
      s.numPages = pdf.numPages
    })

    // Clear the canvas container
    if (canvasContainerRef.current) {
      canvasContainerRef.current.innerHTML = ''
    }

    // Render the PDF pages
    const renderPage = async (pageNum: number) => {
      try {
        // Get the page
        const page = await pdf.getPage(pageNum)
        // Get the viewport
        const viewport = page.getViewport({ scale: zoomLevel })

        // Create a canvas element
        const canvas = document.createElement('canvas')
        // Get the canvas context
        const canvasContext = canvas.getContext('2d')
        if (!canvasContext) return

        // Set the canvas properties
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas['className'] = `${pageNum}-${pdfUrl}`
        canvas.style.display = 'block'
        canvas.style.marginBottom = '20px'

        // Render the page
        const renderContext = {
          canvasContext,
          viewport,
        }

        // Render the page
        const renderPromise = page.render(renderContext).promise

        await renderPromise

        // Append the canvas to the container
        if (canvasContainerRef.current) {
          canvasContainerRef.current.appendChild(canvas)
          setState((s) => {
            s.totalHeight += canvas.height
          })
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error rendering page ${pageNum}:`, error)
      }
    }

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      await renderPage(pageNum)
    }

    const container = canvasContainerRef.current
    if (container) {
      const handleScroll = () => {
        // Check if user scrolled near the bottom (offset 10px)
        if (
          container.scrollTop + container.clientHeight >=
          container.scrollHeight - 10
        ) {
          EvaluatorEditorStore.update((s) => {
            s.hasVisitedLastPage = true
          })
          container.removeEventListener('scroll', handleScroll)
        }
      }
      container.addEventListener('scroll', handleScroll)
    }

    // Set the state to indicate that the pages have been rendered
    setState((s) => {
      s.isRenderingPages = false
      s.isLoading = false
    })
  }

  // Load the PDF document
  const loadPDF = async () => {
    // Set the loading state
    setState((s) => {
      s.isLoading = true
      s.loadingError = null
    })

    // Clear the canvas container
    if (canvasContainerRef.current) {
      canvasContainerRef.current.innerHTML = ''
    }

    try {
      // Load the PDF document
      const pdf = await pdfjsLib.getDocument(pdfUrl).promise
      pdfRef.current = pdf
      // Set the state to indicate that the PDF has been loaded
      setState((s) => {
        s.isLoading = false
      })
    } catch (error) {
      setState((s) => {
        s.isLoading = false
        s.loadingError = 'Failed to load PDF.'
      })
    }
  }

  // Load the PDF document on mount and when the PDF URL changes
  useEffect(() => {
    if (isMobile) return
    loadPDF()
  }, [pdfUrl])

  // Render the pages when the PDF document is loaded and the canvas container is available
  useEffect(() => {
    if (
      pdfRef.current &&
      !state.isRenderingPages &&
      !state.isLoading &&
      canvasContainerRef.current
    ) {
      renderPages()
    }
  }, [zoomLevel, pdfRef.current, state.isLoading])

  /**
   * Increases the zoom level of the evaluator editor by 0.2, up to a maximum of 2.8.
   * If the zoom level is already at or above 2.8, it will not increase further.
   * Also checks and handles the visibility of the panel based on the new zoom level.
   */
  const handleZoomIn = (): void => {
    EvaluatorEditorStore.update((s) => {
      if (s.zoomLevel < 2.8) {
        // max zoom level constraint
        s.zoomLevel = Math.min(s.zoomLevel + 0.2, 2.8)
        checkAndHandlePanelVisibility(s.zoomLevel)
      }
    })
  }

  /**
   * Handles the zoom out functionality for the evaluator editor.
   * Decreases the zoom level by 0.2, ensuring it does not go below the minimum constraint of 0.5.
   * Also checks and handles the visibility of the panel based on the updated zoom level.
   * This function updates the `zoomLevel` state in the `NewEvaluatorEditorStore`.
   */
  const handleZoomOut = (): void => {
    EvaluatorEditorStore.update((s) => {
      if (s.zoomLevel > 0.5) {
        // min zoom level constraint
        s.zoomLevel = Math.max(s.zoomLevel - 0.2, 0.5)
        checkAndHandlePanelVisibility(s.zoomLevel)
      }
    })
  }

  /**
   * Checks the current zoom level and handles the visibility of the panel.
   * If the zoom level is greater than 1.8 and the panel is currently open,
   * it will update the state to close the panel.
   */
  const checkAndHandlePanelVisibility = (zoomLevel: number): void => {
    if (zoomLevel > 1.8 && EvaluatorEditorStore.getRawState().panelOpen) {
      EvaluatorEditorStore.update((s) => {
        s.panelOpen = false
      })
    }
  }

  if (state.isLoading) {
    return <Spinner />
  }

  if (state.loadingError) {
    return <div>{state.loadingError}</div>
  }

  const shouldShowApprovalConfirmation =
    !state.isLoading &&
    state.numPages > 0 &&
    !['completed', 'needReevaluation'].includes(booklet?.status || '')

  return (
    <>
      <StyledPdfContainer className="pdfContainer">
        {/* PDF */}
        <div
          ref={canvasContainerRef}
          style={{ width: '100%', height: '100%', overflowY: 'auto' }}
        />
        {/*  Approval Confirmation */}
        {shouldShowApprovalConfirmation && (
          <BookletApprovalConfirmation
            hasReviewedAllPages={hasReviewedAllPages}
          />
        )}
        {/* Zoom Controls */}
        <StyledZoomControlsContainer>
          <Stack direction="row" spacing={2}>
            {/* Zoom In */}
            <StyledZoomIconButton
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.5}
            >
              <Remove />
            </StyledZoomIconButton>
            {/* Zoom Out */}
            <StyledZoomIconButton
              onClick={handleZoomIn}
              disabled={zoomLevel >= 2.8}
            >
              <Add />
            </StyledZoomIconButton>
          </Stack>
        </StyledZoomControlsContainer>
      </StyledPdfContainer>
    </>
  )
}

const BookletApprovalConfirmation = ({
  hasReviewedAllPages,
}: {
  hasReviewedAllPages: boolean
}): JSX.Element => {
  return (
    <>
      <StyledCheckBoxStack>
        <Checkbox
          color="default"
          checked={hasReviewedAllPages}
          onChange={() =>
            EvaluatorEditorStore.update((s) => {
              s.hasReviewedAllPages = !s.hasReviewedAllPages
            })
          }
        />
        <Typography
          sx={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}
        >
          I confirm that I have thoroughly reviewed all the pages of the
          booklet.
        </Typography>
      </StyledCheckBoxStack>
    </>
  )
}
